// Score display helpers (raster only, docs/05-raster-grading.md §3).
//
// Grading is always Hessen raster: the authoritative per-criterion combination
// runs server-side (server/grading/raster.py) and is surfaced via
// `usePaperRatings`. This module only mirrors the Notenpunkte → score-format
// translation (`format_np`) for live display, and `papers.score_override` is a
// manual Notenpunkte value (0–15).

import type { ScoreFormat } from "../api/types";

/** Notenpunkte are always on the 0–15 scale. */
export const NP_MAX = 15;

const AF_LETTER: Record<number, string> = { 1: "A", 2: "B", 3: "C", 4: "D", 5: "E", 6: "F" };

/** Canonical Notenpunkte (0–15) → Notenstufe (1–6). Half-up rounding first. */
export function npToGrade(np: number): number {
  const n = Math.floor(np + 0.5);
  if (n >= 13) return 1;
  if (n >= 10) return 2;
  if (n >= 7) return 3;
  if (n >= 4) return 4;
  if (n >= 1) return 5;
  return 6;
}

export interface FormattedScore {
  value: number | null;
  label: string | null;
}

/** Translate an internal Notenpunkte value to the project's display format.
 * Mirrors `raster.format_np`. */
export function formatNp(np: number | null | undefined, scoreFormat: ScoreFormat): FormattedScore {
  if (np == null) return { value: null, label: null };
  if (scoreFormat === "0-15") {
    const r = Math.floor(np + 0.5);
    return { value: r, label: String(r) };
  }
  if (scoreFormat === "0-100") {
    const pct = Math.round((np / 15) * 100);
    return { value: pct, label: String(pct) };
  }
  const grade = npToGrade(np);
  if (scoreFormat === "A-F") return { value: grade, label: AF_LETTER[grade] };
  return { value: grade, label: String(grade) }; // 1-6
}
